import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  ArrowRight,
  ArrowUpRight,
  Github,
  Layers,
  Target,
  Activity,
  ChevronRight,
  Terminal
} from 'lucide-react';

// ============================================================================
// STYLES - Inline CSS for the distinctive "Precision Forge" aesthetic
// ============================================================================
const styles = `
  :root {
    --forge-black: #050505;
    --forge-charcoal: #0a0a0a;
    --forge-dark: #111111;
    --forge-surface: #1a1a1a;
    --forge-border: #252525;
    --forge-muted: #666666;
    --forge-text: #e5e5e5;
    --forge-white: #fafafa;
    --forge-orange: #f97316;
    --forge-orange-bright: #fb923c;
    --forge-orange-glow: rgba(249, 115, 22, 0.15);
    --forge-blue: #3b82f6;
    --forge-green: #22c55e;
    --font-display: 'Syne', sans-serif;
    --font-body: 'Inter Tight', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
  }

  .landing-page {
    font-family: var(--font-body);
    background: var(--forge-black);
    color: var(--forge-text);
    min-height: 100vh;
    overflow-x: hidden;
  }

  .landing-page * {
    font-family: inherit;
  }

  /* Typography */
  .font-display {
    font-family: var(--font-display) !important;
  }

  .font-mono {
    font-family: var(--font-mono) !important;
  }

  /* Grid overlay effect */
  .grid-overlay {
    background-image:
      linear-gradient(rgba(249, 115, 22, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(249, 115, 22, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
  }

  /* Noise texture */
  .noise-overlay::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    opacity: 0.03;
    pointer-events: none;
    mix-blend-mode: overlay;
  }

  /* Glow effects */
  .glow-orange {
    box-shadow: 0 0 40px rgba(249, 115, 22, 0.15), 0 0 80px rgba(249, 115, 22, 0.05);
  }

  .text-glow {
    text-shadow: 0 0 40px rgba(249, 115, 22, 0.3);
  }

  /* Animations */
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }

  @keyframes pulse-glow {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  @keyframes scan-line {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }

  @keyframes counter-tick {
    0% { transform: translateY(0); }
    10% { transform: translateY(-2px); }
    20% { transform: translateY(0); }
  }

  @keyframes spark {
    0% { opacity: 1; transform: translateY(0) scale(1); }
    100% { opacity: 0; transform: translateY(-100px) scale(0); }
  }

  @keyframes fade-up {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slide-in-left {
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  }

  .animate-fade-up {
    animation: fade-up 0.8s ease-out forwards;
  }

  .animate-slide-in {
    animation: slide-in-left 0.8s ease-out forwards;
  }

  .animation-delay-100 { animation-delay: 0.1s; }
  .animation-delay-200 { animation-delay: 0.2s; }
  .animation-delay-300 { animation-delay: 0.3s; }
  .animation-delay-400 { animation-delay: 0.4s; }
  .animation-delay-500 { animation-delay: 0.5s; }

  /* Hardware card specific */
  .hardware-card {
    background: linear-gradient(135deg, var(--forge-surface) 0%, var(--forge-dark) 100%);
    border: 1px solid var(--forge-border);
    position: relative;
    overflow: hidden;
  }

  .hardware-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--forge-orange), transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .hardware-card:hover::before {
    opacity: 1;
  }

  /* Button styles */
  .btn-primary {
    background: linear-gradient(135deg, var(--forge-orange) 0%, #ea580c 100%);
    color: white;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
  }

  .btn-primary::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #fb923c 0%, var(--forge-orange) 100%);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .btn-primary:hover::before {
    opacity: 1;
  }

  .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 40px rgba(249, 115, 22, 0.3);
  }

  .btn-secondary {
    background: transparent;
    border: 1px solid var(--forge-border);
    color: var(--forge-text);
    transition: all 0.3s;
  }

  .btn-secondary:hover {
    border-color: var(--forge-orange);
    color: var(--forge-orange);
    background: var(--forge-orange-glow);
  }

  /* Stat counter effect */
  .stat-value {
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
  }

  /* Technical marker */
  .tech-marker {
    width: 8px;
    height: 8px;
    border: 2px solid var(--forge-orange);
    background: var(--forge-black);
  }

  /* Section divider */
  .section-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--forge-border), transparent);
  }

  /* Scroll indicator */
  .scroll-indicator {
    animation: float 2s ease-in-out infinite;
  }
`;

// ============================================================================
// SPARK PARTICLE COMPONENT
// ============================================================================
const SparkParticle = ({ delay, left, duration }) => (
  <div
    className="absolute w-1 h-1 rounded-full bg-orange-500"
    style={{
      left: `${left}%`,
      bottom: '20%',
      animation: `spark ${duration}s ease-out ${delay}s infinite`,
      boxShadow: '0 0 6px 2px rgba(249, 115, 22, 0.6)'
    }}
  />
);

// ============================================================================
// NAVBAR
// ============================================================================
const Navbar = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Features', href: '#features' },
    { label: 'Process', href: '#process' },
    { label: 'Hardware', href: '#hardware' },
    { label: 'Community', href: '#community' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#050505]/95 backdrop-blur-md border-b border-[#252525]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
                <span className="font-display font-bold text-white text-xl">F</span>
              </div>
              <div className="absolute inset-0 bg-orange-500 rounded-lg blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
            </div>
            <span className="font-display font-semibold text-xl text-white tracking-tight">
              ForgeML
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-[#666666] hover:text-white font-medium text-sm tracking-wide transition-colors relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-orange-500 group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={() => navigate('/signin')}
              className="text-[#666666] hover:text-white font-medium text-sm transition-colors"
            >
              Sign in
            </button>
            <button
              onClick={() => navigate('/signin')}
              className="btn-primary px-5 py-2.5 rounded-lg text-sm relative z-10"
            >
              <span className="relative z-10">Get Started</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-white"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-t border-[#252525]">
          <div className="px-6 py-6 space-y-4">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="block text-[#e5e5e5] font-medium py-2"
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <div className="pt-4 border-t border-[#252525] space-y-3">
              <button
                onClick={() => navigate('/signin')}
                className="block w-full text-center text-[#e5e5e5] font-medium py-2"
              >
                Sign in
              </button>
              <button
                onClick={() => navigate('/signin')}
                className="btn-primary w-full py-3 rounded-lg relative"
              >
                <span className="relative z-10">Get Started</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  // Generate spark particles
  const sparks = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: 15 + Math.random() * 70,
    delay: Math.random() * 3,
    duration: 2 + Math.random() * 2
  }));

  return (
    <section ref={containerRef} className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]" />
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute inset-0 noise-overlay" />

      {/* Radial glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, transparent 70%)'
        }}
      />

      {/* Spark particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {sparks.map((spark) => (
          <SparkParticle key={spark.id} {...spark} />
        ))}
      </div>

      {/* Geometric accents */}
      <div className="absolute top-40 left-10 w-32 h-32 border border-[#252525] rotate-45 opacity-20" />
      <div className="absolute bottom-40 right-20 w-24 h-24 border border-orange-500/20 rotate-12" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left column - Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="animate-fade-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-[#252525] bg-[#111111]/50">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="font-mono text-xs text-green-500">LIVE</span>
                </span>
                <span className="w-px h-4 bg-[#252525]" />
                <span className="font-mono text-xs text-[#666666]">Open Source Platform</span>
              </div>
            </div>

            {/* Headline */}
            <div className="animate-fade-up opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
              <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.1] tracking-tight">
                Make your models
                <br />
                <span className="text-glow" style={{ color: 'var(--forge-orange)' }}>
                  run faster
                </span>
              </h1>
            </div>

            {/* Description */}
            <div className="animate-fade-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              <p className="text-lg text-[#888888] leading-relaxed max-w-lg">
                Get optimized implementations for your ML models, tuned for your target hardware.
                Real benchmarks, verified correctness, zero hassle.
              </p>
            </div>

            {/* CTAs */}
            <div className="animate-fade-up opacity-0 flex flex-wrap gap-4" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              <button
                onClick={() => navigate('/signin')}
                className="btn-primary px-8 py-4 rounded-lg text-base flex items-center gap-2 relative"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Optimizing
                  <ArrowRight className="w-5 h-5" />
                </span>
              </button>
              <button
                onClick={() => navigate('/explore')}
                className="btn-secondary px-8 py-4 rounded-lg text-base flex items-center gap-2"
              >
                <Terminal className="w-5 h-5" />
                Browse Models
              </button>
            </div>

            {/* Micro stats */}
            <div className="animate-fade-up opacity-0 pt-8" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
              <div className="flex items-center gap-8">
                {[
                  { value: '7', label: 'Hardware Targets' },
                  { value: 'Verified', label: 'Correctness' },
                  { value: 'Open', label: 'Source' }
                ].map((stat, i) => (
                  <div key={stat.label} className="flex items-center gap-3">
                    {i > 0 && <span className="w-px h-8 bg-[#252525]" />}
                    <div>
                      <div className="font-mono font-semibold text-xl text-white stat-value">{stat.value}</div>
                      <div className="font-mono text-xs text-[#666666] uppercase tracking-wider">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Visual */}
          <div className="relative hidden lg:block">
            <div className="animate-fade-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              {/* Terminal-like card */}
              <div className="relative bg-[#0a0a0a] rounded-xl border border-[#252525] overflow-hidden glow-orange">
                {/* Terminal header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[#252525] bg-[#111111]">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="font-mono text-xs text-[#666666] ml-3">forge optimize --model resnet50</span>
                </div>

                {/* Terminal content */}
                <div className="p-6 font-mono text-sm space-y-3">
                  <div className="flex items-center gap-2 text-[#666666]">
                    <span className="text-orange-500">→</span>
                    <span>Analyzing model architecture...</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#666666]">
                    <span className="text-orange-500">→</span>
                    <span>Found <span className="text-white">47</span> optimizable layers</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#666666]">
                    <span className="text-orange-500">→</span>
                    <span>Target: <span className="text-blue-400">NVIDIA H100</span></span>
                  </div>
                  <div className="flex items-center gap-2 text-green-500">
                    <span>✓</span>
                    <span>Optimization complete</span>
                  </div>
                  <div className="pt-3 border-t border-[#252525]">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#666666]">Speedup achieved:</span>
                      <span className="text-orange-500 font-semibold">3.2x faster</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating metric card */}
              <div className="absolute -bottom-6 -left-6 bg-[#111111] rounded-lg border border-[#252525] p-4 shadow-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <div className="font-mono text-xs text-[#666666]">THROUGHPUT</div>
                    <div className="font-mono font-semibold text-white">+215%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

// ============================================================================
// FEATURES SECTION
// ============================================================================
const FeaturesSection = () => {
  const features = [
    {
      icon: Layers,
      title: 'Graph-level architecture view',
      description: 'Visualize your entire model as an interactive computation graph. See layer connections, data flow, and identify optimization opportunities at a glance.',
      metric: 'Full model visibility'
    },
    {
      icon: Terminal,
      title: 'Built-in code editor',
      description: 'Write and test kernel optimizations directly in the browser. Syntax highlighting, auto-complete, and instant feedback—no local setup needed.',
      metric: 'Edit in browser'
    },
    {
      icon: Target,
      title: 'Layer-by-layer breakdown',
      description: 'Drill down into individual layers. View FLOPs, parameters, and existing optimizations. Compare implementations side by side.',
      metric: 'Deep inspection'
    },
    {
      icon: Activity,
      title: 'Live benchmarking',
      description: 'Run your optimizations against real hardware and see results in real-time. Compare before and after, track speedup across versions.',
      metric: 'Real-time results'
    }
  ];

  return (
    <section id="features" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 grid-overlay opacity-50" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="tech-marker rotate-45" />
              <span className="font-mono text-xs text-[#666666] tracking-widest uppercase">The Platform</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl text-white">
              Tools built for
              <br />
              <span style={{ color: 'var(--forge-orange)' }}>performance engineers</span>
            </h2>
          </div>
          <div className="hidden sm:block">
            <a
              href="#process"
              className="flex items-center gap-2 text-[#666666] hover:text-white transition-colors group"
            >
              <span className="font-mono text-sm">See how it works</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="hardware-card rounded-xl p-6 group hover:border-[#333333] transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                    <Icon className="w-6 h-6 text-orange-500" />
                  </div>
                  <span className="font-mono text-xs text-[#666666]">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-display font-semibold text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-[#888888] leading-relaxed mb-4">{feature.description}</p>
                <div className="pt-4 border-t border-[#252525]">
                  <span className="font-mono text-xs text-orange-500">{feature.metric}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// PROCESS SECTION - Clean Numbered Cards (No Lines)
// ============================================================================
const ProcessSection = () => {
  const steps = [
    {
      num: '01',
      title: 'Pick your model',
      desc: 'Select a model from our registry or explore community-optimized architectures. ResNet, BERT, LLaMA, and more.'
    },
    {
      num: '02',
      title: 'Pick your hardware',
      desc: 'Select target devices—H100, A100, MI300X, or edge. One model, multiple hardware-specific optimizations.'
    },
    {
      num: '03',
      title: 'Optimize kernels',
      desc: 'View and contribute layer-by-layer kernel optimizations. Community-driven, expert-level implementations.'
    },
    {
      num: '04',
      title: 'Benchmark & verify',
      desc: 'Run real hardware benchmarks with golden output verification. Trust the results, ship with confidence.'
    }
  ];

  return (
    <section id="process" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 bg-[#0a0a0a]" />
      <div className="absolute inset-0 grid-overlay opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header - Left aligned */}
        <div className="max-w-2xl mb-16 sm:mb-20">
          <div className="flex items-center gap-3 mb-4">
            <div className="tech-marker rotate-45" />
            <span className="font-mono text-xs text-[#666666] tracking-widest uppercase">How it works</span>
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-white mb-4">
            Four steps to
            <span style={{ color: 'var(--forge-orange)' }}> faster inference</span>
          </h2>
          <p className="text-lg text-[#888888]">
            No PhD required. Browse, optimize, benchmark. Your models run faster in minutes.
          </p>
        </div>

        {/* Steps Grid - 4 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={step.num}
              className="group relative"
            >
              {/* Card */}
              <div className="h-full bg-gradient-to-b from-[#141414] to-[#0f0f0f] rounded-2xl border border-[#1f1f1f] p-6 sm:p-8 transition-all duration-300 hover:border-[#2a2a2a] hover:translate-y-[-4px]">
                {/* Large Step Number */}
                <div className="mb-6">
                  <span
                    className="font-display font-bold text-6xl sm:text-7xl transition-colors duration-300"
                    style={{
                      color: 'transparent',
                      WebkitTextStroke: index === 0 ? '2px var(--forge-orange)' : '1.5px #333333',
                      opacity: index === 0 ? 1 : 0.8
                    }}
                  >
                    {step.num}
                  </span>
                </div>

                {/* Content */}
                <h3 className="font-display font-semibold text-xl text-white mb-3 group-hover:text-orange-50 transition-colors">
                  {step.title}
                </h3>
                <p className="text-[#777777] text-sm leading-relaxed">
                  {step.desc}
                </p>

                {/* Hover glow effect */}
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    background: 'radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(249, 115, 22, 0.06), transparent 40%)'
                  }}
                />
              </div>

              {/* Arrow connector for desktop (between cards, not through them) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-[#333333]">
                    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-[#666666] font-mono text-sm">
            Average optimization time: <span className="text-white">under 5 minutes</span>
          </p>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// HARDWARE SECTION
// ============================================================================
const HardwareSection = () => {
  const hardware = [
    { name: 'NVIDIA H100', type: 'Data Center GPU', mem: '80GB HBM3', bw: '3.35 TB/s', color: '#22c55e' },
    { name: 'NVIDIA A100', type: 'Data Center GPU', mem: '80GB HBM2e', bw: '2.0 TB/s', color: '#3b82f6' },
    { name: 'AMD MI300X', type: 'Data Center GPU', mem: '192GB HBM3', bw: '5.3 TB/s', color: '#ef4444' },
    { name: 'RTX 4090', type: 'Consumer GPU', mem: '24GB GDDR6X', bw: '1.0 TB/s', color: '#a855f7' },
    { name: 'Intel Xeon', type: 'Server CPU', mem: 'DDR5', bw: 'AMX', color: '#06b6d4' },
    { name: 'Edge/Mobile', type: 'Embedded', mem: 'Optimized', bw: 'Inference', color: '#f97316' }
  ];

  return (
    <section id="hardware" className="relative py-32">
      <div className="absolute inset-0 grid-overlay opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="tech-marker rotate-45" />
              <span className="font-mono text-xs text-[#666666] tracking-widest uppercase">Hardware</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl text-white">
              Real silicon,
              <br />
              <span style={{ color: 'var(--forge-orange)' }}>real results</span>
            </h2>
          </div>
          <p className="text-[#888888] max-w-md lg:text-right">
            Every benchmark runs on actual hardware. No emulators, no approximations—just
            real performance data you can trust.
          </p>
        </div>

        {/* Hardware grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hardware.map((hw) => (
            <div
              key={hw.name}
              className="hardware-card rounded-xl p-5 group hover:border-[#333333] transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: hw.color, boxShadow: `0 0 12px ${hw.color}50` }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-semibold text-white truncate">{hw.name}</h3>
                    <ArrowUpRight className="w-4 h-4 text-[#666666] group-hover:text-orange-500 transition-colors flex-shrink-0" />
                  </div>
                  <p className="font-mono text-xs text-[#666666] mt-1">{hw.type}</p>
                  <div className="flex gap-4 mt-3 pt-3 border-t border-[#252525]">
                    <div>
                      <div className="font-mono text-xs text-[#666666]">MEM</div>
                      <div className="font-mono text-sm text-[#888888]">{hw.mem}</div>
                    </div>
                    <div>
                      <div className="font-mono text-xs text-[#666666]">BW</div>
                      <div className="font-mono text-sm text-[#888888]">{hw.bw}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA SECTION
// ============================================================================
const CTASection = () => {
  const navigate = useNavigate();

  return (
    <section id="community" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] to-[#0a0a0a]" />

      {/* Accent glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, transparent 60%)'
        }}
      />

      <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="tech-marker rotate-45" />
          <span className="font-mono text-xs text-[#666666] tracking-widest uppercase">Get Started</span>
          <div className="tech-marker rotate-45" />
        </div>

        <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
          Ready to ship
          <br />
          <span className="text-glow" style={{ color: 'var(--forge-orange)' }}>faster models?</span>
        </h2>

        <p className="text-lg text-[#888888] max-w-2xl mx-auto mb-10">
          Get started in under a minute. No credit card required,
          no complex setup. Just faster inference, immediately.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <button
            onClick={() => navigate('/signin')}
            className="btn-primary px-10 py-4 rounded-lg text-base flex items-center gap-2 relative"
          >
            <span className="relative z-10 flex items-center gap-2">
              Start for Free
              <ChevronRight className="w-5 h-5" />
            </span>
          </button>
          <a
            href="https://github.com"
            className="btn-secondary px-8 py-4 rounded-lg text-base flex items-center gap-2"
          >
            <Github className="w-5 h-5" />
            View on GitHub
          </a>
        </div>

        {/* Social proof */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-[#888888]">
              <span className="text-white font-medium">Real hardware</span> benchmarks
            </span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-[#333333]" />
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <span className="text-[#888888]">
              <span className="text-white font-medium">Verified</span> correctness
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FOOTER
// ============================================================================
const Footer = () => {
  return (
    <footer className="relative border-t border-[#252525]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <span className="font-display font-bold text-white text-sm">F</span>
            </div>
            <span className="font-display font-semibold text-white">ForgeML</span>
          </a>

          {/* Links */}
          <nav className="flex items-center gap-6">
            {['Features', 'Process', 'Hardware', 'Community'].map((link) => (
              <a
                key={link}
                href={`#${link.toLowerCase()}`}
                className="font-mono text-sm text-[#666666] hover:text-white transition-colors"
              >
                {link}
              </a>
            ))}
          </nav>

          {/* Social */}
          <div className="flex items-center gap-4">
            <a href="https://github.com" className="text-[#666666] hover:text-white transition-colors">
              <Github className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-[#252525] text-center">
          <p className="font-mono text-xs text-[#666666]">
            &copy; {new Date().getFullYear()} ForgeML. Built for the ML community.
          </p>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const LandingPage = () => {
  return (
    <>
      <style>{styles}</style>
      <div className="landing-page">
        <Navbar />
        <main>
          <HeroSection />
          <FeaturesSection />
          <ProcessSection />
          <HardwareSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default LandingPage;
